/**
 * ErrorBoundary global — GUIA_REACT_v2 §6.1.
 *
 * Captura errores de render en el árbol React y los reporta a Sentry si está
 * configurado. Muestra un fallback minimalista con CTA para recargar.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureException } from '../lib/monitoring';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack });
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-etribe-blue text-white p-8"
      >
        <div className="max-w-md text-center">
          <p className="font-heading text-[12px] tracking-[0.18em] uppercase text-etribe-yellow mb-4">
            Algo salió mal
          </p>
          <h1 className="font-heading text-3xl mb-4 text-white">
            La tribu está revisando el problema.
          </h1>
          <p className="text-white/80 mb-8">
            Ocurrió un error inesperado. Intenta recargar la página; si persiste, escríbenos a{' '}
            <a href="mailto:contacto@etribe.mx" className="text-etribe-yellow underline">
              contacto@etribe.mx
            </a>
            .
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="bg-etribe-yellow text-etribe-blue font-bold px-6 py-3 rounded-[10px] hover:-translate-y-0.5 transition-transform"
          >
            Recargar la página
          </button>
        </div>
      </div>
    );
  }
}
